import React from 'react';
import _ from 'lodash';
import fp from 'lodash/fp';

import Moment from 'moment';
import { compose } from 'redux';

// components
import { Box, Divider, Grid } from '@mui/material';
import { styled, makeStyles, useTheme } from '@mui/styles';
import { CurrencyAmount } from '@cowbell/ui/core';
import { CoverageDetails } from '../../../_tabs/quotesPolicyCommon/CoverageDetails';
import { LegendCheckbox } from '../../../_tabs/quotesTab/QuoteDetails';
import { DetailsHeaderMapping } from '../../../../utility/CoverageInfoMapping';
import CoverageGroups from '../../../_statics/250/coverages/groups.statics.json';

// hocs, utils
import withRouter from '../../../../components/hocs/withRouter';
import { use250Context } from '../../../agencies/quotes/250/prime250.context';
import { withPrime250 } from '../../../agencies/quotes/250/withPrime250';
import { withDetailsDependencies } from '../../../_tabs/quotesPolicyCommon/withDetailsDependencies';
import DescTooltip from '../../../../components/DescTooltip';
import {
  toUniversalDate,
  toUniversalUtcDate,
} from '../../../../utils/date.utils';
import { REDACTED_FIELD } from '../../../../config/logrocket';

export const P250DrawerDetails = compose(
  withRouter,
  // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
  withPrime250(),
  withDetailsDependencies('quote')
)(({ data, quote }: any) => {
  const { coverages } = use250Context();
  const classes = useStyles();
  const theme = useTheme();

  const grouped = React.useMemo(() => {
    return fp.flow(
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      fp.filter((coverage) => Boolean(coverage.groupId)),
      fp.groupBy('groupId')
    )(coverages);
  }, [coverages]);

  if (!data) {
    return null;
  }

  return (
    <>
      <P250DrawerHeader quote={quote} industry={data.primaryIndustry} />

      <P250DrawerQuoteDetails
        data={data}
        coverageData={data.initial250RequestData}
      />

      <Box style={{ width: '100%' }}>
        <Box className={classes.covHeading}>
          <Box style={{ fontSize: '1.2rem' }}>Coverage Details and Limits</Box>

          <Box
            className="list--unstyled flex--spaced"
            style={{ justifyContent: 'flex-start' }}
          >
            <li>
              <LegendCheckbox label="Mandatory" variant="filled" checked />
            </li>
            <li>
              <LegendCheckbox label="Selected" checked />
            </li>
            <li>
              <LegendCheckbox label="Available" />
            </li>
          </Box>
        </Box>

        {Object.keys(grouped).map((groupId) => (
          <CoverageDetails
            key={groupId}
            // @ts-expect-error TS(2322): Type '{ key: string; id: string; group: any; group... Remove this comment to see the full error message
            id={groupId}
            group={CoverageGroups[groupId]}
            groupedCoverages={grouped}
            background={theme.palette.drawer.background}
          />
        ))}

        <Divider style={{ marginTop: '1rem', width: '100%' }} />

        <div className={classes.price}>
          <span style={{ paddingRight: '1.5rem' }}>Premium:</span>
          {data.agencyStatus === 'REFERRED' ? (
            'Referred'
          ) : (
            <CurrencyAmount amount={data.totalPremium} currency={data.currency} />
          )}
        </div>
      </Box>
    </>
  );
});

const useStyles = makeStyles(({ config, palette }) => ({
  covHeading: {
    background: palette.drawer.background,
    color: palette.primary.contrastText,
    padding: '0.5rem 1.3rem',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '5px',
    textAlign: 'left',
    fontWeight: 'normal',
    letterSpacing: 'normal',
    fontStretch: 'normal',
    position: 'relative',
    top: '2rem',
  },
  price: {
    fontSize: config.textSizes.canon,
    color: palette.primary.contrastText,
    fontWeight: config.weights.bold,
    textAlign: 'right',
    paddingRight: '2rem',
    paddingBottom: '2rem',
    paddingTop: '2rem',
  },
}));

export const P250DrawerQuoteDetails = ({ data, coverageData }: any) => {
  const classes = useQuoteDetailStyles();

  if (_.isUndefined(coverageData)) {
    return null;
  }

  const effectiveDate = Moment(coverageData.effectiveDate);
  const endDate = Moment(coverageData.endDate);
  const duration = Moment.duration(endDate.diff(effectiveDate));

  const quote = {
    'Quote Expires': toUniversalUtcDate(data.expiresOn),
    'Quote Created': toUniversalUtcDate(data.created),
    'Policy Term': `${Math.round(duration.asDays())} Days`,
    'Policy Holder Name':
      !_.isUndefined(data.customerFirstName) &&
      `${data.customerFirstName} ${data.customerLastName}`,
    'Policy Holder Email': data.customerEmail,
  };

  const amount = {
    'Aggregate Limit': (
      <CurrencyAmount
        amount={coverageData.limit}
        currency={data.currency}
        minimumFractionDigits={0}
      />
    ),
    Deductible: (
      <CurrencyAmount
        amount={coverageData.deductible}
        currency={data.currency}
        minimumFractionDigits={0}
      />
    ),
  };

  return (
    <Box display="flex" justifyContent="space-between" width="100%" p={1}>
      <Box width="48%">
        {_.entries(quote).map(([key, value]) => (
          <div key={key} className="flex--spaced">
            <div>{key}</div>
            <div className={REDACTED_FIELD}>{value}</div>
          </div>
        ))}
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box width="48%">
        <div className={`flex--spaced ${classes.summary}`}>
          <div>Coverage Start Date</div>
          <div>{toUniversalDate(effectiveDate)}</div>
        </div>
        <div className="flex--spaced">
          <div>Coverage End Date</div>
          <div>{toUniversalDate(endDate)}</div>
        </div>

        {_.entries(amount).map(([key, value]) => {
          const tooltip = DetailsHeaderMapping[key].tooltip || '';
          return (
            <div key={key} className="flex--spaced">
              <div>
                {key}
                <DescTooltip tooltip={tooltip} />
              </div>
              <div>{value}</div>
            </div>
          );
        })}
        <div className={`flex--spaced ${classes.premium}`}>
          Premium:
          <div>
            {data.agencyStatus === 'REFERRED' ? (
              'Referred'
            ) : (
              <CurrencyAmount
                amount={data.totalPremium}
                currency={data.currency}
              />
            )}
          </div>
        </div>
      </Box>
    </Box>
  );
};

const useQuoteDetailStyles = makeStyles(({ config, palette }) => ({
  summary: {
    fontSize: config.textSizes.normal,
    color: palette.primary.contrastText,
    fontWeight: 'normal',
    letterSpacing: 'normal',
    lineHeight: 1.8,
  },
  premium: {
    fontSize: config.textSizes.primer,
    lineHeight: 1.4,
  },
}));

const P250DrawerHeader = ({ quote, industry }: any) => {
  return (
    <Grid container>
      <Grid item md={6}>
        <HeaderTextLeft style={{ fontWeight: 600 }}>
          {quote.quoteNumber}
        </HeaderTextLeft>
        <HeaderTextLeft>{quote.name}</HeaderTextLeft>
      </Grid>
      {/* @ts-expect-error TS(2769): No overload matches this call. */}
      <Grid item md={6} align="right">
        <HeaderTextRight>Industry {quote.naicsCode}</HeaderTextRight>
        <HeaderTextRight>{industry}</HeaderTextRight>
      </Grid>
      <Divider style={{ width: '100%', margin: '1rem 0' }} />
    </Grid>
  );
};

const HeaderTextLeft = styled(Box)(({ theme }) => ({
  fontSize: theme.config.textSizes.tertia,
}));
const HeaderTextRight = styled(Box)(({ theme }) => ({
  fontSize: theme.config.textSizes.tertia,

  color: theme.palette.text.title,
}));
